import { Injectable, isDevMode } from '@angular/core';
import { FilterItem } from '../shared/filter-item';
import { AssetValueFilterComponent } from '../ui/filters/asset-value-filter/asset-value-filter.component';
import { AssetIndustrySectorFilterComponent } from '../ui/filters/asset-industry-sector-filter/asset-industry-sector-filter.component';
import { AssetLocationFilterComponent } from '../ui/filters/asset-location-filter/asset-location-filter.component';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { IMarker } from 'src/app/azure-maps/interfaces';
import { catchError, finalize, map, shareReplay, take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AssetSearchService {

    private countries$: Observable<ICountry[]> | null = null;
    private assetTypes$: Observable<IAssetType[]> | null = null;
    private sectors$: Observable<ISector[]> | null = null;
    private countriesWarmupStarted = false;

    constructor(private http: HttpClient) { }


    saveSearchDefinition(requestPayload: AssetSearchDefinitionRequest): Observable<any> { //} ISearchResponse{
        //https://eicdatastream4.eic-dev.com:4401
        const apiUrl = '/api/searches/asset';
        return this.http.post<any>(apiUrl, requestPayload);
    }


    getSearchResults(searchResultsUrl: string): Observable<any> { // { IAssetSummary[]
        const results = this.http.get(searchResultsUrl);
        return results;
    }

    getMapResults(mapResultsUrl: string): Observable<any> { // { IAssetSummary[]
        const results = this.http.get(mapResultsUrl);
        return results;
    }


    fetchAssets(partName: string): Observable<IAssetName[]> {
        if (!partName) return of([])
        return this.http.get<IAssetName[]>(`/api/searches/assetNames?partName=${partName}`);
    }


    getAssetNames(partName: string): Observable<IAssetName[]> {
        const apiUrl = `/api/searches/assetNames?partName='{partName}'`;
        const results = this.http.get(apiUrl) as Observable<IAssetName[]>;
        return results;
    }


    fetchOperatorNames(query: string): Observable<IOperatorName[]> {
        const apiUrl = `/api/searches/operatornames?partname=${query}`;
        const results = this.http.get(apiUrl) as Observable<IOperatorName[]>;

        return results;
    }


    fetchContractorNames(query: string): Observable<IContractorName[]> {
        const apiUrl = `/api/searches/contractornames?partname=${query}`;
        const results = this.http.get(apiUrl) as Observable<IContractorName[]>;

        return results;
    }


    getAssetStatuses(): Observable<IAssetStatus[]> {
        return of(
            [
                //{ "assetStatusID": 0, "assetStatus": "Unknown" },
                { "assetStatusID": 1, "assetStatus": "Operational" },
                //{ "assetStatusID": 2, "assetStatus": "In Production" },
                { "assetStatusID": 3, "assetStatus": "Operation Suspended" },
                { "assetStatusID": 4, "assetStatus": "Production Suspended" },
                { "assetStatusID": 5, "assetStatus": "Production Ceased" },
                { "assetStatusID": 6, "assetStatus": "Decommissioning" },
                { "assetStatusID": 7, "assetStatus": "Decommissioned" }
            ]
        );

    }


    getAssetTypes(): Observable<IAssetType[]> {
        if (!this.assetTypes$) {
            this.assetTypes$ = this.http.get<IAssetType[]>('/api/assetTypes?includeSubtypes=true')
                .pipe(
                    shareReplay(1) // Caches the latest emitted value
                );
        }
        return this.assetTypes$;
    }


    getCountries(): Observable<ICountry[]> {
        if (!this.countries$) {
            this.countries$ = this.http.get<ICountry[]>('/api/countries')
                .pipe(
                    map(countries => countries.filter(country => country.id !== 0)),
                    shareReplay(1) // Caches the latest emitted value
                );
        }
        return this.countries$;
    }

    warmCountriesInBackground(): void {
        if (this.countriesWarmupStarted) {
            return;
        }

        this.countriesWarmupStarted = true;
        if (isDevMode()) {
            console.debug('[AssetSearchService] Countries warmup started');
        }

        this.getCountries()
            .pipe(
                take(1),
                tap(() => {
                    if (isDevMode()) {
                        console.debug('[AssetSearchService] Countries warmup succeeded');
                    }
                }),
                catchError((error) => {
                    if (isDevMode()) {
                        console.debug('[AssetSearchService] Countries warmup failed', error);
                    }
                    return of([]);
                }),
                finalize(() => {
                    if (isDevMode()) {
                        console.debug('[AssetSearchService] Countries warmup completed');
                    }
                })
            )
            .subscribe();
    }


    getEnergySectors(): Observable<ISector[]> {
        if (!this.sectors$) {
            this.sectors$ = this.http.get<ISector[]>('/api/industrysectors')
                .pipe(
                    shareReplay(1) // Caches the latest emitted value
                );
        }
        return this.sectors$;
    }


    getAssetTypesWithSubTypes(): Observable<IAssetType[]> {
        if (!this.assetTypes$) {
            const allAssetTypesUrl = '/api/assettypes?includeSubtypes=true';
            this.assetTypes$ = this.http.get<IAssetType[]>(allAssetTypesUrl)
                .pipe(
                    tap(a => {
                        const i = a;
                    }),
                    shareReplay(1) // Caches the latest emitted value
                );
        }

        return this.assetTypes$;
    }

}




export interface IAssetName {
    assetID: number;
    name: string;
}

export interface IAssetStatus {
    assetStatusID: number;
    assetStatus: string;
}

export interface IAssetSubtype {
    assetSubtypeID: number;
    legacyKey: number;
    subtype: string;
    layerId: string;
    layerType: LayerTypes;
    marker?: IMarker;
}

export interface IAssetType {
    assetTypeID: number;
    assetType: string;
    subtypes?: IAssetSubtype[];
    layerId?: string;
    layerType: LayerTypes;
    marker?: IMarker;
    hasSubtypes: boolean;
}

export enum LayerTypes {
    point = 1,
    line = 2,
    shape = 3
}

export interface AssetSummary {
    geoJsonCoordinates: any;
    AssetId: number;
    AssetName: string;
    AssetType: string;
    AssetSubtype: string;
    EnergySector: string;
    Status: string;
    Operator: string;
    Country: string;
    Region: string;
    AssetUrl: string;
    ProductionStartDate?: Date
}

export interface Paging {
    pageNumber: number;
    pageSize: number;
}

export interface Sorting {
    sortBy: string;
    sortOrder: string;
}

export interface AssetSearchDefinitionRequest {
    AssetSearchId?: string; // Guid as a string
    Criteria: any; // Using 'any' for JObject
    Paging: Paging | any;
    Sorting: Sorting | any;
}

export interface ICriteria {
    hasCriteria: boolean
}

export interface IAssetNameCriteria extends ICriteria {
    partName?: string,
    searchText?: string;
    assetNames?: IAssetName[],
    assetIds?: number[],
    limit?: number,
    startsWith?: boolean
}

export interface ILocationCriteria extends ICriteria {
    regionIds?: number[],
    countyIds?: number[],
    countries?: ICountry[],
    localityIds?: number[],
    location?: string
}

export interface IIndustryCriteria extends ICriteria {
    industrySectors?: ISector[],
    industryIds?: number[],
    energySectors?: ISector[],
    energySectorIds?: number[],
}

export interface IOperatorCriteria extends ICriteria {
    operators?: string[],
}

export interface IContractorCriteria extends ICriteria {
    contractors?: string[],
}


export interface IAssetStatusCriteria extends ICriteria {
    assetStatuses?: IAssetStatus[];
    assetStatusIds?: number[],
}

export interface IAssetTypeCriteria extends ICriteria {
    assetTypeIds?: number[];
    assetTypes?: IAssetType[];
}
export interface IAssetTypeSelection {
    types: number[],
    subtypes: number[],
    layers: string[]
}


export interface ICountry {
    id: number;
    name: string;
    disabled: boolean;
    displayFirst: boolean;
}

export interface ISector {
    id: number;
    name: string;
}

export interface IOperatorName {
    operatorID: number;
    operatorName: string;
}

export interface IContractorName {
    contractorID: number;
    contractorName: string;
}
